import { MatchAccessibility } from '@okletsplay/core/src/modules/match/enums';
import { UserEntity } from 'app/interfaces/entity';
import { MatchModeSwitcher } from 'modules/matches/components/CreateMatchModal/MatchModeSwitcher';
import { MatchPlayersSelection } from 'modules/matches/components/CreateMatchModal/MatchPlayersSelection';
import { MatchAccordionDescription } from 'modules/matches/components/MatchAccordionDescription/MatchAccordionDescription';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Accordion } from 'shared/components/Accordion';
import { MatchFormFields } from 'modules/matches/interfaces/forms';
import { DynamicSchemaBlock } from 'shared/components/Form/DynamicSchemaBlock';
import { GameEntity } from '@okletsplay/core';
import { Box, FormControlLabel, Grid, Switch } from '@mui/material';
import { MATCH_PLAYERS_DEFAULT_SETTINGS } from 'modules/matches/constants';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';

interface MatchPlayersFormProps {
  game: GameEntity | undefined;
  onAccordionClick: () => void;
  isExpanded: boolean;
  opponents?: UserEntity[];
}

export const MatchPlayersForm: FC<MatchPlayersFormProps> = ({
  game,
  onAccordionClick,
  isExpanded,
  opponents,
}) => {
  const { control, watch, setValue, trigger, formState } = useFormContext<MatchFormFields>();
  const playersErrorMessage = formState.errors?.players?.message;

  const accessibility = watch('accessibility');
  const playersSettings = watch('playersSettings') ?? MATCH_PLAYERS_DEFAULT_SETTINGS;

  const description = useMemo(
    () =>
      accessibility === MatchAccessibility.Private
        ? 'Private Match'
        : 'Open Match - Opponent Invite Optional',
    [accessibility]
  );

  const playersSettingsProperties = game?.matchesConfiguration?.playersSettings?.properties;

  const onPlayersSelect = useCallback(
    (userIds: string[]) => {
      setValue('players', userIds);
      trigger('players');
    },
    [setValue, trigger]
  );

  useEffect(() => {
    trigger('players');
  }, [trigger, playersSettings?.minPlayersCount, playersSettings?.maxPlayersCount]);

  useEffect(() => {
    if (!game?.matchesConfiguration?.playersSettings) {
      setValue('playersSettings', undefined);
      trigger('players');
    }
  }, [game?.matchesConfiguration?.playersSettings, setValue, trigger]);

  return (
    <Accordion
      title="Players"
      description={<MatchAccordionDescription text={description} />}
      onChange={onAccordionClick}
      expanded={isExpanded}
      contentTopPadding={2}
    >
      {playersErrorMessage ? (
        <Box mb={1}>
          <ErrorMessage error={playersErrorMessage} withSidePadding={false} />
        </Box>
      ) : null}

      <Grid container ml={1.5} mb={1.5}>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="accessibility"
            render={({ field: { onChange, value } }) => (
              <MatchModeSwitcher value={value} onChange={onChange} />
            )}
          />
        </Grid>
        {accessibility === MatchAccessibility.Public ? (
          <Grid item xs={6}>
            <Controller
              control={control}
              name="isJoinApproveRequired"
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={<Switch checked={value} onChange={onChange} />}
                  label="Manual Join Approve Required?"
                />
              )}
            />
          </Grid>
        ) : null}
      </Grid>

      {playersSettingsProperties ? (
        <Box mb={1}>
          <DynamicSchemaBlock
            properties={playersSettingsProperties}
            rootPropertyName="playersSettings"
            isEditAllowed={true}
            variant="create"
          />
        </Box>
      ) : null}

      <Controller
        control={control}
        name="players"
        render={() => (
          <MatchPlayersSelection
            onChange={onPlayersSelect}
            existingPlayers={opponents}
            accessibility={accessibility}
            playersSettings={playersSettings}
          />
        )}
      />
    </Accordion>
  );
};
